import { returnFormattedDate } from "@/lib/globalFormatterHelper";
import { formatNumberWithCommas } from "@/lib/utilities";
import {
  returnContractAmountDisplay,
  returnIndicativeContractNotional,
} from "../helper/futureTicketHelper";
import {
  ITicketFieldsProps,
  TICKET_EMPTY_VALUE,
  returnIndicativePrice,
} from "../helper/ticketSharedHelper";
import TicketRow from "../ticketFields/TicketRow";
import { useFutureTicket } from "./useFutureTicket";

// Read-only contract reference data. Becomes the Trade Creation Security Details panel
// content.

function returnNotionalDisplay(notional: number | null): string {
  if (notional === null) return TICKET_EMPTY_VALUE;
  return formatNumberWithCommas(notional);
}

function returnPriceDisplay(price: number | null): string {
  return price === null ? TICKET_EMPTY_VALUE : String(price);
}

const FutureDetailFields = ({ ticketId }: ITicketFieldsProps) => {
  const { ticket, contract } = useFutureTicket(ticketId);
  const price = returnIndicativePrice(contract);
  const notional = returnIndicativeContractNotional(
    ticket.contractCount,
    contract?.contractAmount ?? null,
  );

  return (
    <div
      data-testid="FutureDetailFieldsContainer"
      id="FutureDetailFieldsContainer"
      className="flex flex-col gap-2"
    >
      <TicketRow label="Trade Date" testId="FutureTicketTradeDateRow">
        <div className="text-xs dark:text-gray-200">
          {ticket.tradeDate
            ? returnFormattedDate(ticket.tradeDate)
            : TICKET_EMPTY_VALUE}
        </div>
      </TicketRow>

      <TicketRow label="Exchange" testId="FutureTicketExchangeRow">
        <div className="text-xs dark:text-gray-200">
          {contract?.exchange || TICKET_EMPTY_VALUE}
        </div>
      </TicketRow>

      <TicketRow label="Currency" testId="FutureTicketCurrencyRow">
        <div className="text-xs dark:text-gray-200">
          {contract?.currency || TICKET_EMPTY_VALUE}
        </div>
      </TicketRow>

      <TicketRow label="Contract Size" testId="FutureTicketContractSizeRow">
        <div className="text-xs dark:text-gray-200">
          {returnContractAmountDisplay(contract, TICKET_EMPTY_VALUE)}
        </div>
      </TicketRow>

      {/* Spec F11: futures have an expiry, not a maturity. */}
      <TicketRow label="Expiry Date" testId="FutureTicketExpiryDateRow">
        <div className="text-xs dark:text-gray-200">
          {contract?.expiryDate
            ? returnFormattedDate(contract.expiryDate)
            : TICKET_EMPTY_VALUE}
        </div>
      </TicketRow>

      {/* Blank today by design: sp_GetFuturesWithPrices has no ICE futures prices. */}
      <TicketRow label="Price (mid)" testId="FutureTicketPriceRow">
        <div className="text-xs dark:text-gray-200">
          {returnPriceDisplay(price)}
        </div>
      </TicketRow>

      {/* Face notional, deliberately not priced: bond and rate futures quote differently. */}
      <TicketRow label="Notional" testId="FutureTicketNotionalRow">
        <div className="text-xs dark:text-gray-200">
          {returnNotionalDisplay(notional)}
        </div>
      </TicketRow>
    </div>
  );
};

export default FutureDetailFields;
